export default function animationsComponent() {
    return {
        observer: null,
        animatedElements: new Set(),
        
        init() {
            // Early exit if no elements to animate
            const elementsToAnimate = document.querySelectorAll("[data-animate]");
            if (elementsToAnimate.length === 0) return;
            
            // Immediately hide elements to prevent flash
            this.hideElementsImmediately(elementsToAnimate);
            
            this.observer = new IntersectionObserver(
                this.handleIntersection.bind(this),
                {
                    rootMargin: "0px 0px -40px 0px",
                    threshold: 0,
                }
            );
            
            // Batch DOM operations for better performance
            this.initializeElements(elementsToAnimate);
        },
        
        handleIntersection(entries) {
            // Use requestAnimationFrame to batch DOM updates
            requestAnimationFrame(() => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !this.animatedElements.has(entry.target)) {
                        this.animateElement(entry.target);
                        this.animatedElements.add(entry.target);
                        this.observer.unobserve(entry.target);
                    }
                });
            });
        },
        
        animateElement(target) {
            const baseDelay = parseFloat(target.getAttribute("data-delay")) || 0;
            const staggerIncrement = 0.18;
            const animateChildren = target.hasAttribute("data-animate-elements");
            
            if (animateChildren) {
                this.animateChildren(target, baseDelay, staggerIncrement);
            } else {
                this.animateParent(target, baseDelay);
            }
        },
        
        animateChildren(target, baseDelay, staggerIncrement) {
            const children = target.children;
            
            // Animate each child with proper delay
            for (let i = 0; i < children.length; i++) {
                const child = children[i];
                const totalDelay = (baseDelay + i * staggerIncrement) * 1000; // Convert to milliseconds
                
                setTimeout(() => {
                    child.classList.add("animate-effect-in");
                }, totalDelay);
            }
        },
        
        animateParent(target, baseDelay) {
            const delayMs = baseDelay * 1000; // Convert seconds to milliseconds
            
            setTimeout(() => {
                target.classList.add("animate-effect-in");
            }, delayMs);
        },
        
        hideElementsImmediately(elements) {
            // Synchronously hide elements to prevent flash
            elements.forEach((el) => {
                const animateChildren = el.hasAttribute("data-animate-elements");
                
                if (animateChildren) {
                    Array.from(el.children).forEach(child => {
                        child.style.opacity = "0";
                        child.style.transform = "translateY(50px)";
                    });
                } else {
                    el.style.opacity = "0";
                    el.style.transform = "translateY(50px)";
                }
            });
        },
        
        initializeElements(elements) {
            // Use DocumentFragment for batch DOM operations
            const updates = [];
            
            elements.forEach((el) => {
                const animateChildren = el.hasAttribute("data-animate-elements");
                
                if (animateChildren) {
                    // Collect child updates for transitions
                    Array.from(el.children).forEach(child => {
                        updates.push({
                            element: child,
                            styles: {
                                transition: "opacity 0.6s ease-out, transform 0.6s ease-out"
                            }
                        });
                    });
                } else {
                    updates.push({
                        element: el,
                        styles: {
                            transition: "opacity 0.6s ease-out, transform 0.6s ease-out"
                        }
                    });
                }
                
                this.observer.observe(el);
            });
            
            // Apply transition styles in a single frame
            requestAnimationFrame(() => {
                updates.forEach(({ element, styles }) => {
                    Object.assign(element.style, styles);
                });
            });
        },
        
        // Cleanup method for component destruction
        destroy() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
            this.animatedElements.clear();
        },
        
        // Public method to manually trigger animation
        triggerAnimation(selector) {
            const element = typeof selector === 'string' 
                ? document.querySelector(selector) 
                : selector;
                
            if (element && !this.animatedElements.has(element)) {
                this.animateElement(element);
                this.animatedElements.add(element);
                if (this.observer) {
                    this.observer.unobserve(element);
                }
            }
        }
    };
}